import { useEffect, useState, useRef } from 'react';
import {
  Form,
  Table,
  Input,
  Select,
  Button,
  Space,
  Tag,
  Typography,
  message
} from 'antd';
import {
  CaretUpOutlined, CaretDownOutlined, CheckOutlined,
  CloseOutlined, RedoOutlined
} from '@ant-design/icons';
import { getConversationList, retryAIReview } from '../services/conversationService.js';
import moment from 'moment';
import { useNavigate, useLocation } from 'react-router-dom';
import { getExamine } from '../services/ctaService.js';
import { getCompanyList } from '../services/userService.js';
import ResponsiveFilterContainer from '../components/common/ResponsiveFilterContainer.jsx';
import { useGlobalState } from '../hooks/useGlobalState.js'
import { config } from '../config/index.js';
const ConversationList = () => {
  const { Text } = Typography;
  const location = useLocation();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [data, setData] = useState({ groupsRows: [], total: 0 });
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState(location.state?.filters || {});
  const [expandedGroups, setExpandedGroups] = useState([]);
  const [pagination, setPagination] = useState({
    current: location.state?.pageInfo?.current || 1,
    pageSize: location.state?.pageInfo?.pageSize || 10,
  });
  const [companies, setCompanies] = useState([]);
  const [disabledRetryIds, setDisabledRetryIds] = useState(new Set());
  const { userProfile } = useGlobalState();
  const { API_BASE_URL } = config;
  const filtersRef = useRef(filters);
  const paginationRef = useRef(pagination);
  const isCompanyUser = userProfile?.user?.userStatus === "Company User";
  useEffect(() => {
    filtersRef.current = filters;
  }, [filters]);

  useEffect(() => {
    paginationRef.current = pagination;
  }, [pagination]);

  const hiddenColumnsForCompanyUser = [
    "companyName",
    "hasQuestionBankMsg",
    "auditStatus",
    "aiReview"
  ];
  const columns = [
    {
      title: 'Group',
      dataIndex: 'Group',
    },
    {
      title: '	Conversation ID',
      dataIndex: 'conversationId',
      key: 'conversationId',
      render: (text, record) => {
        const isChild = !record.isParent;
        return <Text style={{ paddingLeft: isChild ? 24 : 0 }}>{text}</Text>;
      },
    },
    {
      title: 'Username',
      dataIndex: 'userName',
      key: 'userName',
      render: (text, record) => {
        const isChild = !record.isParent;
        return <Text style={{ paddingLeft: isChild ? 24 : 0 }}>{text}</Text>;
      },
    },
    {
      title: 'Conversation Name',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => {
        const isChild = !record.isParent;
        return <Text style={{ paddingLeft: isChild ? 24 : 0 }}>{text}</Text>;
      },
    },
    {
      title: 'Has CTA',
      dataIndex: 'hasCta',
      key: 'hasCta',
      render: (value, record) => {
        const isChild = !record.isParent;
        if (value) {
          return (
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                handleJumpToCTADetail(record);
              }}
              style={{ color: '#1890ff', paddingLeft: isChild ? 24 : 0 }}
            >
              Yes
            </a>
          );
        } else {
          return 'No';
        }
      },
    },
    {
      title: 'Reference',
      dataIndex: 'reference',
      key: 'reference',
      render: (text, record) => {
        return <Text>{text}</Text>;
      },
    },
    /* {
       title: 'Feedback',
       key: 'reportId',
       render: (_, record) => {
         const isChild = !record.isParent;
         return (
           <Space style={{ paddingLeft: isChild ? 24 : 0 }}>
             {record.feedback?.positive && <Tag color="success">Agree</Tag>}
             {record.feedback?.negative && <Tag color="error">Disagree</Tag>}
           </Space>
         );
       },
     },*/

    {
      title: 'Company',
      dataIndex: 'companyName',
      key: 'companyName',
      render: (companyName, record) => {
        const isChild = !record.isParent;
        const company = companyName ? companyName : '-';
        return <Text style={{ paddingLeft: isChild ? 24 : 0 }}>{company}</Text>;
      },
    },

    {
      title: 'Time',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (createdAt, record) => {
        const isChild = !record.isParent;
        const time = createdAt
          ? moment(createdAt).format('YYYY-MM-DD HH:mm:ss')
          : '-';
        return <Text style={{ paddingLeft: isChild ? 24 : 0 }}>{time}</Text>;
      },
    },
    {
      title: 'Conversation Status',
      key: 'conversationStatus',
      dataIndex: 'conversationStatus',
      render: (status, record) => {
        const isChild = !record.isParent;
        let color = 'default';
        let displayText = status;
        const statusMap = {
          进行中: 'In Progress',
          已结束: 'Ended',
          已报告: 'Reported',
          已删除: 'Deleted',
          未知: 'Unknown',
          Exception: 'Exception',
        };
        displayText = statusMap[status] || status;
        switch (status) {
          case '进行中':
            color = 'processing';
            break;
          case '已结束':
            color = 'success';
            break;
          case '已报告':
            color = 'warning';
            break;
          case '已删除':
            color = 'red';
            break;
          case 'Exception':
            color = 'red';
            break;
        }
        return (
          <Space style={{ paddingLeft: isChild ? 24 : 0 }}>
            <Tag color={color}>{displayText}</Tag>
          </Space>
        );
      },
    },
    {
      title: 'Question Bank',
      dataIndex: 'hasQuestionBankMsg',
      key: 'hasQuestionBankMsg',
      render: (text) => {
        return <Text>{text == '0' ? 'No' : 'Yes'}</Text>;
      },
    },
    {
      title: 'Audit Status',
      dataIndex: 'auditStatus',
      key: 'auditStatus',
      render: (text, record) => {
        const isChild = !record.isParent;
        return (
          <Text style={{ paddingLeft: isChild ? 24 : 0 }}>
            {text == '0' ? 'No' : 'Yes'}
          </Text>
        );
      },
    },
    {
      title: 'AI Review',
      dataIndex: 'aiReview',
      key: 'aiReview',
      render: (text, record) => {
        const isChild = !record.isParent;

        if (!text) {
          return '—';
        }

        return (
          <Text style={{ paddingLeft: isChild ? 24 : 0 }}>
            {text === 'CORRECT' && (
              <CheckOutlined style={{ color: 'green' }} />
            )}

            {text === 'MANUAL' && (
              <Button
                type="link"
                size="small"
                icon={
                  <RedoOutlined
                    style={{
                      color: disabledRetryIds.has(record.conversationId) ? '#bfbfbf' : '#fa8c16'
                    }}
                  />
                }
                disabled={disabledRetryIds.has(record.conversationId)}
                onClick={() => {
                  setDisabledRetryIds(prev => {
                    const next = new Set(prev);
                    next.add(record.conversationId);
                    return next;
                  });
                  handleRetryAiReview(record.conversationId);
                }}
              />
            )}
            {text !== 'CORRECT' && text !== 'MANUAL' && (
              <CloseOutlined style={{ color: 'red' }} />
            )}
          </Text>
        );
      },
    },
    {
      title: 'Token Usage',
      dataIndex: 'tokenCount',
      key: 'tokenCount',
      render: (text, record) => {
        const isChild = !record.isParent;
        return (
          <div style={{ paddingLeft: isChild ? 24 : 0 }}>
            <div>{text}</div>
            {record.auditToken && !isCompanyUser && (
              <div style={{ color: '#888', marginTop: 4 }}>{record.auditToken}</div>
            )}
          </div>
        );
      },
    },
    {
      title: 'Operation',
      key: 'operation',
      render: (_, record) => {
        const isChild = !record.isParent;
        return (
          <Space size="middle">
            <a
              href={`/conversations/${record.externalRef}`}
              onClick={(e) => {
                e.preventDefault();
                handleNavigate(record);
              }}
              style={{ paddingLeft: isChild ? 24 : 0 }}
            >
              <Button type="link" style={{ padding: 0 }}>
                View
              </Button>
            </a>
          </Space>
        );
      },
    },
  ].filter(col => !(userProfile?.user?.userStatus === "Company User" && hiddenColumnsForCompanyUser.includes(col.dataIndex)));

  useEffect(() => {
    fetchConversationGroup();
    getCompany();

  }, []);

  useEffect(() => {
    if (
      location.state?.filters &&
      Object.keys(location.state.filters).length > 0
    ) {
      form.setFieldsValue(location.state.filters);
    }
  }, [location.state?.filters]);

  const toggleGroup = (groupId) => {
    setExpandedGroups((prev) => {
      const next = prev.includes(groupId)
        ? prev.filter((id) => id !== groupId)
        : [...prev, groupId];
      updateGroupedRows(data.rawRows || [], next);
      return next;
    });
  };
  const handleRetryAiReview = async (conversationId) => {
    try {
      const response = await retryAIReview(conversationId, userProfile?.user?.userId);
      if (response?.code === 200) {
        if (response?.msg === "AI review retry has been successfully triggered") {
          message.success(response.msg);
        } else {
          message.error(response.msg);
        }
      } else {
        message.error(response.msg);
      }
    } catch (error) {
      console.error('AI Review Failed:', error);
      message.error('AI Review Failed, please try again.');
    }
  }

  const getCompany = async () => {
    try {
      const response = await getCompanyList();
      setCompanies(response.rows);
    } catch (error) {
      console.error('Fail to fetch company list:', error);
    }
  };


  const fetchConversationGroup = async (
    params = filters,
    newPagination = pagination
  ) => {
    if (params.searchValue == undefined) {
      params.searchValue = '';
    }
    setLoading(true);
    try {
      const response = await getConversationList({
        ...params,
        searchValue: encodeBase64(params.searchValue),
        pageNum: newPagination.current,
        pageSize: newPagination.pageSize,
        companyId: params.companyId ?? userProfile?.user?.companyId ?? null,
      });

      if (response?.code === 200) {
        const rows = response.rows.map((item, index) => ({
          key: (newPagination.current - 1) * newPagination.pageSize + index + 1,
          ...item,
        }));

        const groups = new Map();
        for (const row of rows) {
          const isNullHash = row.imgHash === null;
          const groupKey = isNullHash ? row.conversationId : row.imgHash;
          if (!groups.has(groupKey)) {
            groups.set(groupKey, [row]);
          } else {
            groups.get(groupKey).push(row);
          }
        }

        let groupIdCounter = 1;
        const newExpandedGroups = [];
        const groupedData = [];

        Array.from(groups.entries()).forEach(([_, conversations]) => {
          const parent = {
            ...conversations[0],
            isParent: true,
            groupSize: conversations.length,
            groupId: groupIdCounter,
          };
          newExpandedGroups.push(groupIdCounter); // 默认展开
          groupedData.push(parent);
          groupedData.push(
            ...conversations.slice(1).map((item) => ({
              ...item,
              isParent: false,
              groupId: groupIdCounter,
            }))
          );
          groupIdCounter++;
        });

        setExpandedGroups(newExpandedGroups);
        setData({
          groupsRows: groupedData,
          total: response.total,
          rawRows: rows,
        });
      }
    } catch (error) {
      console.error('Fail to fetch conversation list:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (userProfile?.user?.userStatus == "Company User") {
      console.log("Company User,不建立 SSE 连接");
      return; 
    }
    const url = `${API_BASE_URL}/conversation/connect/${userProfile?.user?.userId}`;
    const eventSource = new EventSource(url, { withCredentials: true });

    eventSource.onopen = () => {
      console.log('SSE Connected');
    };

    eventSource.addEventListener('aiReviewComplete', (event) => {
      console.log('AI Review Completed:', event.data);
      const isSuccess = event.data.includes('Succeeded');
      if (isSuccess) {
        message.success(event.data);
      } else {
        message.error('AI Review Failed');
        const newDisabledRetryIds = new Set(disabledRetryIds); 
        newDisabledRetryIds.delete(event.data); 
        setDisabledRetryIds(newDisabledRetryIds); 
      }
      fetchConversationGroup(filtersRef.current, paginationRef.current);
    });

    eventSource.onerror = (error) => {
      console.error('SSE Error:', error);
      eventSource.close();
    };

    return () => {
      eventSource.close();
    };
  }, []);

  const updateGroupedRows = (rawRows, currentExpandedGroups) => {
    const groups = new Map();
    for (const row of rawRows) {
      const isNullHash = row.imgHash === null;
      const groupKey = isNullHash ? row.conversationId : row.imgHash;
      if (!groups.has(groupKey)) {
        groups.set(groupKey, [row]);
      } else {
        groups.get(groupKey).push(row);
      }
    }

    let groupIdCounter = 1;
    const groupedRows = [];

    Array.from(groups.entries()).forEach(([_, conversations]) => {
      const parent = {
        ...conversations[0],
        isParent: true,
        groupSize: conversations.length,
        groupId: groupIdCounter,
      };
      groupedRows.push(parent);

      if (currentExpandedGroups.includes(groupIdCounter)) {
        groupedRows.push(
          ...conversations.slice(1).map((item) => ({
            ...item,
            isParent: false,
            groupId: groupIdCounter,
          }))
        );
      }

      groupIdCounter++;
    });

    setData((prev) => ({ ...prev, groupsRows: groupedRows }));
  };

  const encodeBase64 = (message) => btoa(unescape(encodeURIComponent(message)));
  //const decodeBase64 = (base64) => decodeURIComponent(escape(atob(base64)));

  const handleSearch = (values) => {
    if (values.searchValue == undefined) {
      values.searchValue = '';
    }
    const params = {
      ...values,
      //searchValue: encodeBase64(values.searchValue),
      conversationStatusList: values.conversationStatusList,
      feedbackType: values.feedbackType,
    };
    setFilters(params);
    setPagination({ ...pagination, current: 1 });
    fetchConversationGroup(params, pagination);
  };

  const handleJumpToCTADetail = async (record) => {
    try {
      const examine = await getExamine(
        record.externalRef,
        record.conversationId
      );
      if (examine.ctas && examine.ctas.length > 0) {
        const allCtaContent = [];
        examine.ctas.forEach((ctaItem) => {
          const ctaContent = ctaItem.cta || ctaItem.finalCta;
          if (ctaContent && ctaContent.trim()) {
            allCtaContent.push(ctaContent.trim());
          }
        });
        if (allCtaContent.length > 0) {
          const searchContent = allCtaContent.join('\n');

          const params = new URLSearchParams({
            searchCta: searchContent,
            autoSearch: 'true',
          });
          window.open(`/cta?${params.toString()}`, '_blank');
        } else {
          message.warning('该会话中没有有效的CTA内容');
        }
      } else {
        message.warning('该会话中没有找到CTA信息');
      }
    } catch (error) {
      console.error('获取CTA信息失败:', error);
      message.error('跳转失败，请重试');
    }
  };

  const handleReset = () => {
    form.resetFields();
    const newPagination = { current: 1, pageSize: 10 };
    setFilters({});
    setPagination(newPagination);
    fetchConversationGroup({}, newPagination);
  };

  const handleTableChange = (pagination) => {
    setPagination({
      current: pagination.current,
      pageSize: pagination.pageSize,
    });
    fetchConversationGroup(filters, pagination);
  };

  const handleNavigate = (record) => {
    navigate(`/conversations/${record.externalRef}`, {
      state: {
        conversation: record,
        from: {
          pathname: '/conversations',
          pageInfo: {
            current: pagination.current,
            pageSize: pagination.pageSize,
          },
          filters: { ...filters },
        },
      },
    });
  };

  return (
    <div>
      <ResponsiveFilterContainer
        form={form}
        onSearch={handleSearch}
        onReset={handleReset}
        initialValues={{
          searchValue: '',
          hasCta: '',
          conversationStatusList: [],
          feedbackStatus: '',
          feedbackType: [],
        }}
      >
        <Form.Item name="searchValue" style={{ width: '100%' }}>
          <Input placeholder="Please enter content to search"></Input>
        </Form.Item>

        {userProfile?.user?.userStatus !== "Company User" && <Form.Item name="auditStatus" label="Audit Status">
          <Select
            style={{ width: '100%' }}
            options={[
              { value: '1', label: 'Yes' },
              { value: '0', label: 'No' },
            ]}
          />
        </Form.Item>
        }

        <Form.Item name="hasCta" label="Has CTA">
          <Select
            style={{ width: '100%' }}
            options={[
              { value: 'true', label: 'Yes' },
              { value: 'false', label: 'No' },
            ]}
          />
        </Form.Item>

        {userProfile?.user?.userStatus !== "Company User" &&
          <Form.Item name="companyId" label="Company">
            <Select
              style={{ width: '100%' }}
              allowClear
              options={companies.map((company) => ({
                label: company.companyName,
                value: company.id,
              }))}
            ></Select>
          </Form.Item>}

        <Form.Item name="conversationStatusList" label="Conversation Status">
          <Select
            mode="multiple"
            style={{ width: '100%' }}
            options={[
              { value: '进行中', label: 'In Progress' },
              { value: '已结束', label: 'Ended' },
              { value: '已报告', label: 'Reported' },
              { value: '已删除', label: 'Deleted' },
              { value: 'EXCEPTION', label: 'Exception' },
            ]}
          />
        </Form.Item>

        <Form.Item name="reference" label="Reference">
          <Select
            style={{ width: '100%' }}
            options={[
              { value: 'Web', label: 'Web' },
              { value: 'Email', label: 'Email' },
            ]}
          />
        </Form.Item>
        {userProfile?.user?.userStatus !== "Company User" &&
          <Form.Item name="hasQuestionBankMsg" label="Question Bank">
            <Select
              style={{ width: '100%' }}
              options={[
                { value: '1', label: 'Yes' },
                { value: '0', label: 'No' },
              ]}
            />
          </Form.Item>
        }
      </ResponsiveFilterContainer>

      <Table
        columns={columns}
        dataSource={data.groupsRows}
        loading={loading}
        pagination={{
          current: pagination.current,
          total: data.total,
          pageSize: pagination.pageSize,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total) => `${total} in total`,
        }}
        expandable={{
          expandIcon: ({ expanded, onExpand, record }) => {
            if (record.isParent && (record.groupSize ?? 1) > 1) {
              return (
                <Space size={0} align="center">
                  <Tag
                    onClick={(e) => {
                      onExpand(record, e);
                      toggleGroup(record.groupId);
                    }}
                    style={{
                      background: '#006699',
                      color: 'white',
                      cursor: 'pointer',
                      marginRight: 0,
                      padding: '0 8px',
                      height: 24,
                      lineHeight: '24px',
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    {record.groupSize}
                  </Tag>
                  {expanded ? <CaretUpOutlined /> : <CaretDownOutlined />}
                </Space>
              );
            }
            return null;
          },
          rowExpandable: (record) =>
            record.isParent && (record.groupSize ?? 1) > 1,
        }}
        onRow={(record) => {
          if (!record.isParent) {
            return {
              style: {
                background: '#F0F9FF',
              },
            };
          }
          return {};
        }}
        onChange={handleTableChange}
      />
    </div>
  );
};

export default ConversationList;
